import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid
} from '@mui/material';
import { mavService } from '../services/mavService';

function MAVManagement() {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    product_category: '', region: '', unit_id: 'kg', mav_value_kg_co2e: '',
    source_organization: '', valid_from: '', conservative_adjustment_factor: '1.2'
  });

  useEffect(() => { loadReferences(); }, []);

  const loadReferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mavService.getReferences();
      setReferences(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await mavService.createReference({
        ...form,
        mav_value_kg_co2e: parseFloat(form.mav_value_kg_co2e),
        conservative_adjustment_factor: parseFloat(form.conservative_adjustment_factor)
      });
      setDialogOpen(false);
      setForm({ product_category: '', region: '', unit_id: 'kg', mav_value_kg_co2e: '', source_organization: '', valid_from: '', conservative_adjustment_factor: '1.2' });
      loadReferences();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>MAV Reference Data</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Maximum Applicable Value defaults for missing supplier data (E-ledger Principles 5-6)
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>Add MAV Reference</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>MAV References ({references.length})</Typography>
          {references.length === 0 ? (
            <Alert severity="info">No MAV references configured. Add reference data for product categories.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product Category</TableCell>
                  <TableCell>Subcategory</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell align="right">MAV (kg CO2e)</TableCell>
                  <TableCell align="right">Adj. Factor</TableCell>
                  <TableCell align="right">Adjusted Value</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Valid From</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {references.map((ref) => {
                  const adjusted = parseFloat(ref.mav_value_kg_co2e) * parseFloat(ref.conservative_adjustment_factor);
                  return (
                    <TableRow key={ref.mav_reference_id}>
                      <TableCell>{ref.product_category}</TableCell>
                      <TableCell>{ref.product_subcategory || '-'}</TableCell>
                      <TableCell>{ref.region || 'Global'}</TableCell>
                      <TableCell align="right">{parseFloat(ref.mav_value_kg_co2e).toFixed(4)}</TableCell>
                      <TableCell align="right">{parseFloat(ref.conservative_adjustment_factor).toFixed(2)}x</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{adjusted.toFixed(4)}</TableCell>
                      <TableCell>{ref.source_organization}</TableCell>
                      <TableCell>{ref.valid_from}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add MAV Reference</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Product Category" value={form.product_category} onChange={(e) => setForm({ ...form, product_category: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="MAV Value (kg CO2e)" type="number" value={form.mav_value_kg_co2e}
                onChange={(e) => setForm({ ...form, mav_value_kg_co2e: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Adj. Factor" type="number" inputProps={{ step: 0.1 }}
                value={form.conservative_adjustment_factor} onChange={(e) => setForm({ ...form, conservative_adjustment_factor: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Unit" value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Source Organization" value={form.source_organization}
                onChange={(e) => setForm({ ...form, source_organization: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Valid From" type="date" InputLabelProps={{ shrink: true }}
                value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={!form.product_category || !form.mav_value_kg_co2e || !form.source_organization || !form.valid_from}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MAVManagement;
